import { useCallback, useRef, useState } from "react";

/**
 * Quantised so a sub-pixel or scrollbar-width jitter cannot re-render a whole table body. 8px is
 * the same quantum `contactSurface.tsx` settled on.
 */
const WIDTH_QUANTUM = 8;

export type ContainerWidth = {
  /** Put this on the element whose width decides the layout. */
  ref: (node: HTMLElement | null) => void;
  /** `null` until the observer has measured — callers seed from a media query for the first paint. */
  width: number | null;
};

/**
 * The width of the element this ref is attached to, for layouts that must switch on their CONTAINER
 * rather than on the window.
 *
 * ⚠️ WHY THIS EXISTS. `useMediaQuery(theme.breakpoints.up("md"))` asks "is the WINDOW ≥ 900px",
 * which is the wrong question for anything rendered inside a dialog, a drawer or a panel. The
 * matched-buyers table is the clearest case: it lives in a `maxWidth="md"` dialog, so on a 1440px
 * desktop its content box is ~790px — the media query says "desktop" while the container is
 * narrower than a tablet. `ContactTableNestedRowTabs.tsx` hit the same thing from the other side and
 * added a `compact` prop for it; this is that fix generalised.
 *
 * ResizeObserver via a CALLBACK REF, not `useRef` + `useEffect`: the effect can fire before the node
 * exists on the first render and would then observe nothing. Not `useElementSize` from usehooks-ts
 * either — that one is a window `resize` listener, so it never fires when a dialog opens, which is
 * the only event that matters here. Both traps are documented at `contactSurface.tsx:69-74`, which
 * is where this pattern comes from.
 */
export function useContainerWidth(): ContainerWidth {
  const [width, setWidth] = useState<number | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  const ref = useCallback((node: HTMLElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!node) return;

    const observer = new ResizeObserver((entries) => {
      const measured = entries[0]?.contentRect.width ?? 0;
      // A hidden or not-yet-laid-out element measures 0. Reporting that would flip the caller to its
      // narrowest layout for a frame; staying `null` keeps it on the media-query seed instead.
      if (measured <= 0) return;
      const quantised = Math.round(measured / WIDTH_QUANTUM) * WIDTH_QUANTUM;
      setWidth((current) => (current === quantised ? current : quantised));
    });
    observer.observe(node);
    observerRef.current = observer;
  }, []);

  return { ref, width };
}

export default useContainerWidth;
